/* eslint-disable no-console */

import React, { useState, useEffect } from 'react';
import PageStyle from './style';
import { Input, Select, TableProps, Tooltip, message } from 'antd';
import { Button, Image, Pagination, Table } from 'src/components';
import {
  SearchOutlined,
  DeleteOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { SpinStyle } from 'src/components/style';

import DeleteModal from './delete-modal/DeleteModal';
import FormModal from './form-modal/FormModal';

import { useAppDispatch, useAppSelector } from 'src/stores';
import {
  deletePageContentAction,
  listPageContentAction,
} from 'src/stores/screens/pageContent/pageContent.action';
import { LANGUAGE, PAGE_LIMIT } from 'src/constants';
import { capitalizeFirstLetter } from 'src/utils';
import { FLAG_EN, FLAG_JA, FLAG_KO, FLAG_VI } from 'src/assets/images';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import Breadcrumb from 'src/components/breadcrumb';
import { detailPageSessionAction } from 'src/stores/screens/pageSession/pageSession.action';
import SettingSortOrdersModal from './sort-orders-modal/SortOrdersModal';

const StyledWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PageContentScreen: React.FC = () => {
  const { sessionId } = useParams();
  const dispatch = useAppDispatch();
  const { session } = useAppSelector((state) => state.pageSession);
  const { contents, isLoading } = useAppSelector((state) => state.pageContent);
  const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);
  const [isModalFormOpen, setIsModalFormOpen] = useState(false);
  const [isModalSettingOrderOpen, setIsModalSettingOrderOpen] = useState(false);
  const [langue, setLangue] = useState(LANGUAGE.EN);
  const [currentRecord, setCurrentRecord] =
    useState<PageContent.PageContentType | null>(null);
  const [filter, setFilter] = useState({
    keyword: '',
  });
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchCurrentPageSession();
    fetchData();
  }, [sessionId]);

  useEffect(() => {}, []);

  const fetchData = (page = 1) => {
    if (!sessionId) return;
    const params: PageContent.GetListPageContentParams = cleanFilter({
      ...filter,
      page,
      sorts: 'sort_order-asc,created_at-asc'
    });
    void dispatch(
      listPageContentAction({ data: { ...params, limit: PAGE_LIMIT, sessionId } })
    );
    setCurrentPage(page);
  };

  const fetchCurrentPageSession = async () => {
    if (!sessionId) return;
    await dispatch(detailPageSessionAction({ data: sessionId }));
  };

  const onFilterLangue = (value: string) => {
    setLangue(value);
  };

  const langues = [
    {
      label: (
        <StyledWrapper>
          {' '}
          <Image src={FLAG_EN} /> EN{' '}
        </StyledWrapper>
      ),
      value: LANGUAGE.EN,
    },
    {
      label: (
        <StyledWrapper>
          {' '}
          <Image src={FLAG_VI} /> VI{' '}
        </StyledWrapper>
      ),
      value: LANGUAGE.VI,
    },
    {
      label: (
        <StyledWrapper>
          {' '}
          <Image src={FLAG_JA} /> JA{' '}
        </StyledWrapper>
      ),
      value: LANGUAGE.JA,
    },
    {
      label: (
        <StyledWrapper>
          {' '}
          <Image src={FLAG_KO} /> KO{' '}
        </StyledWrapper>
      ),
      value: LANGUAGE.KO,
    },
  ];

  const columns: TableProps<any>['columns'] = [
    {
      title: '#',
      dataIndex: '#',
      key: '#',
      width: '80px',
      align: 'center',
      render: (id: string, _, index: number) => {
        const serialNumber = (currentPage - 1) * PAGE_LIMIT + (index + 1);
        return (
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '60px',
              whiteSpace: 'nowrap',
            }}
          >
            {serialNumber}
          </span>
        );
      },
    },
    {
      title: (
        <div className="langue-select">
          <div>Title</div>
          <div>
            <Select
              style={{ width: 120 }}
              placeholder="langue"
              maxTagCount="responsive"
              options={langues}
              value={langue}
              onChange={(value) => onFilterLangue(value)}
            />
          </div>
        </div>
      ),
      dataIndex: `title${capitalizeFirstLetter(langue)}`,
      key: `title${capitalizeFirstLetter(langue)}`,
      className: 'min-w-200',
      render: (value: string) => {
        return (
          <p>{value}</p>
        );
      },
    },
    {
      title: 'Description',
      dataIndex: `description${capitalizeFirstLetter(langue)}`,
      key: `description${capitalizeFirstLetter(langue)}`,
      render: (value) => <p>{value}</p>
    },
    {
      title: 'Media',
      dataIndex: 'fileUrl',
      key: 'fileUrl',
      width: '90px',
      align: 'center',
      render: (fileUrl, { fileType }) => {
        const isVideo = fileType?.includes('video')
        if (isVideo) return 'VIDEO'
        return fileUrl && !isVideo && <img src={fileUrl} style={{ width: '60px', height: '60px', objectFit: 'contain' }} />
      }
    },
    {
      title: 'Action',
      key: 'action',
      width: 130,
      align: 'center',
      onCell: () => ({ style: { minWidth: 130 } }),
      render: (_, record) => {
        return (
          <div className="action-box">
            <Tooltip placement="top" title={'Edit content'}>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  onEditUser(record);
                }}
              >
                <EditOutlined />
              </Button>
            </Tooltip>
            <Tooltip placement="top" title={'Delete content'}>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentRecord(record);
                  setIsModalDeleteOpen(true);
                }}
              >
                <DeleteOutlined />
              </Button>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  const cleanFilter = <T extends Record<string, any>>(obj: T): T => {
    const cleanedObj: Partial<T> = {};

    Object.keys(obj).forEach((key) => {
      if (obj[key] !== null && obj[key] !== '') {
        cleanedObj[key as keyof T] = obj[key];
      }
    });

    return cleanedObj as T;
  };

  const onCreate = () => {
    setCurrentRecord(null);
    setIsModalFormOpen(true);
  };

  const onEditUser = (value: PageContent.PageContentType) => {
    setUpUser(value);
    setIsModalFormOpen(true);
  };

  const handleDeleteOk = async () => {
    if (currentRecord?.id) {
      const result: any = await dispatch(
        deletePageContentAction({ data: currentRecord.id })
      );
      setIsModalDeleteOpen(false);
      if (result?.error) {
        message.error('Delete failed');
      } else {
        message.success('Delete success');
        fetchData();
      }
    }
  };

  const setUpUser = (value: PageContent.PageContentType) => {
    setCurrentRecord(value);
  };

  const handleOkFormUser = () => {
    handleCancelModal();
  };

  const onChangePage = (value: number) => {
    fetchData(value);
  };

  const handleCancelModal = () => {
    setIsModalFormOpen(false);
  };

  return !session ? (
    <></>
  ) : (
    <PageStyle className="container">
      <SpinStyle
        spinning={isLoading}
        wrapperClassName="root-spin"
        className="root-spin-component"
      >
        <div className="header-content">
          <Breadcrumb
            breadcrumbItems={[
              {
                name: 'Page',
                path: '/page',
              },
              {
                name: session?.pages?.pageNameEn,
                path: `/page/${session?.pages?.id}`,
              },
              {
                name: session?.titleEn,
                path: `/page/${session?.pages?.id}/${session?.id}`,
              },
            ]}
          />
          <div style={{ display: 'flex', gap: 10 }}>
            <Button className="create-user" onClick={() => setIsModalSettingOrderOpen(true)}>
              Setting Order
            </Button>
            <Button className="create-user" onClick={() => onCreate()}>
              Create
            </Button>
          </div>
        </div>
        <div className="content">
          <div className="filter-box">
            <div className="total-box">Total: {contents?.total}</div>
            <div className="search-box">
              <Input
                placeholder="Search title..."
                prefix={<SearchOutlined />}
                onChange={(e) =>
                  setFilter({ ...filter, keyword: e.target.value.trim() })
                }
                onPressEnter={() => fetchData()}
              />
              <Button onClick={() => fetchData()}>Search</Button>
            </div>
          </div>
          <div className="data-table">
            <Table
              dataSource={contents.records || []}
              columns={columns}
              scroll={{ x: 'auto', y: contents.records.length ? window.innerHeight - 380 : undefined }}
            />
            <Pagination
              total={contents.total}
              onChange={onChangePage}
              current={currentPage}
              pageSize={contents.limit}
            />
          </div>
        </div>
        <DeleteModal
          isModalDeleteOpen={isModalDeleteOpen}
          handleCancel={() => setIsModalDeleteOpen(false)}
          handleOk={handleDeleteOk}
        />
        {isModalFormOpen && (
          <FormModal
            isModalFormOpen={isModalFormOpen}
            handleCancel={handleCancelModal}
            handleOk={handleOkFormUser}
            sessionId={sessionId}
            currentRecord={currentRecord}
            fetchData={fetchData}
            currentPage={currentPage}
          />
        )}

        {isModalSettingOrderOpen && (
          <SettingSortOrdersModal
            isModalFormOpen={isModalSettingOrderOpen}
            handleCancel={() => setIsModalSettingOrderOpen(false)}
            handleOk={() => setIsModalSettingOrderOpen(false)}
            sessionId={sessionId}
            fetchData={fetchData}
          />
        )}
      </SpinStyle>
    </PageStyle>
  );
};

export default PageContentScreen;
